import FileUpload from 'components/atoms/file_upload';
import Loader from 'components/atoms/loader';
import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ProfileState, ProfileTypes } from 'store/modules/profile_store/types';
import {
  createPreferenceForUser,
  deactivateUser,
  destroyPreferenceForUser,
  destroyUserAvatar,
  getUserFilters,
  getUserInfo,
  grantUserFilter,
  resetUserPassword,
  revokeUserFilter,
  updateUserFilters,
  uploadUserAvatar,
  UserFilterEntry,
  UserInfoResponse,
} from 'services/actions/users_actions';
import {
  AlertIcon,
  ButtonIcon,
  Chevron,
  FieldLabel,
  FieldValue,
  FilterCard,
  FilterEmpty,
  FilterHeader,
  FilterRow,
  FilterStatus,
  Hint,
  StatusBadge,
} from './styles';
import { UserProfilePanelProps } from './types';

const PREFERENCE_IDS = {
  noNotificationBw: 1,
  betaTester: 2,
  noBulletinPushNotification: 11,
  noBulletinEmailNotification: 12,
};

type PrefKey =
  | 'no_notification_bw'
  | 'no_bulletin_push_notification'
  | 'no_bulletin_email_notification'
  | 'beta_tester';

type FilterConfirmAction =
  | { kind: 'single'; filter: UserFilterEntry }
  | { kind: 'bulk'; grant: boolean; ids: number[] };

const UserProfilePanel: React.FC<UserProfilePanelProps> = ({
  user: userProp = null,
  onEdit,
  onDeactivated,
  feedbackMessage,
}) => {
  const dispatch = useDispatch();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [info, setInfo] = useState<UserInfoResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pendingPref, setPendingPref] = useState<PrefKey | null>(null);
  const [showResetModal, setShowResetModal] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState<boolean>(false);
  const [deactivating, setDeactivating] = useState<boolean>(false);
  const [deactivateError, setDeactivateError] = useState<string>('');
  const [resetFeedback, setResetFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [avatarError, setAvatarError] = useState<string>('');
  const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
  const [removingAvatar, setRemovingAvatar] = useState<boolean>(false);
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
  const [avatarPickerKey, setAvatarPickerKey] = useState<number>(0);
  const [filters, setFilters] = useState<UserFilterEntry[]>([]);
  const [pendingFilterId, setPendingFilterId] = useState<number | null>(null);
  const [filterFeedback, setFilterFeedback] = useState<{
    id: number;
    type: 'saved' | 'error';
  } | null>(null);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(true);
  const [filterSearch, setFilterSearch] = useState<string>('');
  const [filterConfirm, setFilterConfirm] = useState<FilterConfirmAction | null>(null);
  const [bulkSaving, setBulkSaving] = useState<boolean>(false);
  const [bulkError, setBulkError] = useState<string>('');

  useEffect(() => {
    if (!userProp?.id) return undefined;

    let active = true;
    setLoading(true);
    getUserInfo(userProp.id)
      .then((response) => {
        if (active) setInfo(response.data);
      })
      .catch(() => {
        if (active) setInfo(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [userProp?.id]);

  useEffect(() => {
    if (!userProp?.id) return undefined;

    let active = true;
    getUserFilters(userProp.id)
      .then((response) => {
        if (!active) return;
        const loaded = response?.data?.filters || [];
        setFilters(loaded);
        // Recolhe por padrão quando há muitos filtros, pra não poluir o painel.
        setFiltersOpen(loaded.length <= 8);
      })
      .catch(() => {
        if (active) setFilters([]);
      });

    return () => {
      active = false;
    };
  }, [userProp?.id]);

  useEffect(() => {
    if (resetFeedback?.type !== 'success') return undefined;
    const timer = setTimeout(() => setResetFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [resetFeedback]);

  useEffect(() => {
    if (!filterFeedback) return undefined;
    const timer = setTimeout(() => setFilterFeedback(null), 2500);
    return () => clearTimeout(timer);
  }, [filterFeedback]);

  const togglePreference = async (
    prefKey: PrefKey,
    preferenceId: number,
    currentlyHas: boolean,
  ) => {
    if (!userProp?.id || pendingPref) return;

    setPendingPref(prefKey);
    setInfo((prev) => (prev ? { ...prev, [prefKey]: !currentlyHas } : prev));

    try {
      if (currentlyHas) {
        await destroyPreferenceForUser(preferenceId, userProp.id);
      } else {
        await createPreferenceForUser(preferenceId, userProp.id);
      }
    } catch {
      setInfo((prev) => (prev ? { ...prev, [prefKey]: currentlyHas } : prev));
    } finally {
      setPendingPref(null);
    }
  };

  const handleDeactivate = async () => {
    if (!userProp?.id || deactivating) return;

    setDeactivating(true);
    setDeactivateError('');

    try {
      const response = await deactivateUser(userProp.id);
      const data = response?.data;
      if (data?.success) {
        setShowDeactivateModal(false);
        if (onDeactivated) onDeactivated(data.message);
      } else {
        setDeactivateError(data?.message || 'Não foi possível inativar o usuário.');
      }
    } catch (error: any) {
      setDeactivateError(error?.response?.data?.message || 'Erro ao se comunicar com o servidor.');
    } finally {
      setDeactivating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userProp?.id || resetting) return;

    setResetting(true);
    setResetFeedback(null);

    try {
      const response = await resetUserPassword(userProp.id);
      const data = response?.data;
      if (data?.success) {
        setResetFeedback({ type: 'success', message: data.message });
        setShowResetModal(false);
      } else {
        setResetFeedback({
          type: 'error',
          message: data?.message || 'Não foi possível enviar o e-mail de reset.',
        });
      }
    } catch (error: any) {
      setResetFeedback({
        type: 'error',
        message: error?.response?.data?.message || 'Erro ao se comunicar com o servidor.',
      });
    } finally {
      setResetting(false);
    }
  };

  const toggleFilter = async (filterId: number, currentlyHasAccess: boolean) => {
    if (!userProp?.id || pendingFilterId !== null) return;

    setPendingFilterId(filterId);
    setFilters((prev) =>
      prev.map((f) => (f.id === filterId ? { ...f, has_access: !currentlyHasAccess } : f)),
    );

    try {
      if (currentlyHasAccess) {
        await revokeUserFilter(userProp.id, filterId);
      } else {
        await grantUserFilter(userProp.id, filterId);
      }
      setFilterFeedback({ id: filterId, type: 'saved' });
    } catch {
      setFilters((prev) =>
        prev.map((f) => (f.id === filterId ? { ...f, has_access: currentlyHasAccess } : f)),
      );
      setFilterFeedback({ id: filterId, type: 'error' });
    } finally {
      setPendingFilterId(null);
    }
  };

  // Aplica concessão/remoção em lote num único request: com muitos filtros,
  // um grant/revoke por filtro dispararia uma sincronização com o CRM em cada um.
  const applyBulkFilters = async (grant: boolean, ids: number[]) => {
    if (!userProp?.id || bulkSaving || ids.length === 0) return;

    setBulkSaving(true);
    setBulkError('');
    const previous = filters;
    const idsSet = new Set(ids);
    setFilters((prev) => prev.map((f) => (idsSet.has(f.id) ? { ...f, has_access: grant } : f)));

    try {
      const response = await updateUserFilters(userProp.id, grant ? ids : [], grant ? [] : ids);
      if (!response?.data?.success) throw new Error(response?.data?.message);
    } catch {
      setFilters(previous);
      setBulkError('Não foi possível salvar os filtros. Tente novamente.');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleFilterConfirm = async () => {
    if (!filterConfirm) return;
    setFilterConfirm(null);

    if (filterConfirm.kind === 'single') {
      await toggleFilter(filterConfirm.filter.id, filterConfirm.filter.has_access);
    } else {
      await applyBulkFilters(filterConfirm.grant, filterConfirm.ids);
    }
  };

  const handleAvatarSelect = (file: File) => {
    setAvatarError('');
    setPendingAvatarFile(file);
  };

  const handleAvatarDiscard = () => {
    setPendingAvatarFile(null);
    setAvatarError('');
    // Remonta o seletor para restaurar o preview da foto atual.
    setAvatarPickerKey((key) => key + 1);
  };

  const handleAvatarSave = async () => {
    if (!userProp?.id || !pendingAvatarFile || uploadingAvatar) return;

    setUploadingAvatar(true);
    setAvatarError('');

    try {
      const response = await uploadUserAvatar(userProp.id, pendingAvatarFile);
      const data = response?.data;
      if (data?.success && data.avatar_url) {
        setInfo((prev) =>
          prev ? { ...prev, user: { ...prev.user, avatar_url: data.avatar_url } } : prev,
        );
        if (profile?.id === userProp.id) {
          dispatch({ type: ProfileTypes.SET_AVATAR_URL, avatar_url: data.avatar_url });
        }
        setPendingAvatarFile(null);
      } else {
        setAvatarError(data?.message || 'Falha ao enviar foto.');
      }
    } catch (error: any) {
      setAvatarError(error?.response?.data?.message || 'Erro ao se comunicar com o servidor.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (!userProp?.id || removingAvatar) return;

    setRemovingAvatar(true);
    setAvatarError('');

    try {
      const response = await destroyUserAvatar(userProp.id);
      const data = response?.data;
      if (data?.success) {
        setInfo((prev) => (prev ? { ...prev, user: { ...prev.user, avatar_url: null } } : prev));
        if (profile?.id === userProp.id) {
          dispatch({ type: ProfileTypes.SET_AVATAR_URL, avatar_url: '' });
        }
        setPendingAvatarFile(null);
        setAvatarPickerKey((key) => key + 1);
      } else {
        setAvatarError(data?.message || 'Falha ao remover foto.');
      }
    } catch (error: any) {
      setAvatarError(error?.response?.data?.message || 'Erro ao se comunicar com o servidor.');
    } finally {
      setRemovingAvatar(false);
    }
  };

  const contact = info?.user?.contact_client;

  const formatPhone = (ddd?: string, numero?: string) =>
    ddd || numero ? `(${ddd || ''}) ${numero || ''}` : '-';

  const telefoneFixo =
    contact?.ddd_telefone || contact?.numero_telefone
      ? `${formatPhone(contact?.ddd_telefone, contact?.numero_telefone)}${
          contact?.ramal_telefone ? ` · Ramal ${contact.ramal_telefone}` : ''
        }`
      : '-';

  const visibleFilters = useMemo(() => {
    const term = filterSearch.trim().toLowerCase();
    if (!term) return filters;
    return filters.filter((f) => f.name.toLowerCase().includes(term));
  }, [filters, filterSearch]);

  // Alvo do botão em lote: enquanto houver filtro visível desmarcado, o botão
  // seleciona os que faltam; com todos marcados, passa a desmarcar todos.
  const bulkTarget = useMemo(() => {
    const grant = visibleFilters.some((f) => !f.has_access);
    const ids = visibleFilters.filter((f) => f.has_access !== grant).map((f) => f.id);
    return { grant, ids };
  }, [visibleFilters]);

  const filterConfirmMessage = useMemo(() => {
    if (!filterConfirm) return '';

    if (filterConfirm.kind === 'single') {
      const { filter } = filterConfirm;
      return filter.has_access
        ? `Remover o acesso ao filtro "${filter.name}" deste usuário?`
        : `Conceder o acesso ao filtro "${filter.name}" para este usuário?`;
    }

    const count = filterConfirm.ids.length;
    const plural = count > 1 ? 's' : '';
    const scope = filterSearch.trim() ? ` encontrado${plural} na busca` : '';
    return filterConfirm.grant
      ? `Conceder ${count} filtro${plural}${scope} para este usuário?`
      : `Remover ${count} filtro${plural}${scope} deste usuário?`;
  }, [filterConfirm, filterSearch]);

  const recebeBoletim = info ? !info.no_notification_bw : true;
  const recebeEmail = info ? !info.no_bulletin_email_notification : true;
  const recebePush = info ? !info.no_bulletin_push_notification : true;
  const isBetaTester = !!info?.beta_tester;

  if (loading || !info) {
    return (
      <div className="d-flex justify-content-center py-3">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-100">
      <div className="p-1">
        {feedbackMessage && (
          <div className="alert alert-success py-2 mb-3 d-flex align-items-center" role="alert">
            <AlertIcon className="material-icons mr-2">check_circle</AlertIcon>
            {feedbackMessage}
          </div>
        )}

        <h6 className="font-weight-bold mb-3">Dados</h6>
        <div className="d-flex flex-wrap">
          <div className="mr-4 mb-3">
            <FileUpload
              key={avatarPickerKey}
              accept="image/png, image/jpeg, image/gif"
              maxSizeMB={5}
              initialImageUrl={info.user?.avatar_url || ''}
              onFileSelect={handleAvatarSelect}
            />

            {pendingAvatarFile && (
              <div className="d-flex flex-wrap mt-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm mr-2 mb-2"
                  onClick={handleAvatarSave}
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? 'Salvando…' : 'Salvar foto'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm mb-2"
                  onClick={handleAvatarDiscard}
                  disabled={uploadingAvatar}
                >
                  Descartar
                </button>
              </div>
            )}

            {!pendingAvatarFile && info.user?.avatar_url && (
              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-danger p-0"
                  onClick={handleAvatarRemove}
                  disabled={removingAvatar}
                >
                  <ButtonIcon className="material-icons align-middle mr-1">delete</ButtonIcon>
                  {removingAvatar ? 'Removendo…' : 'Remover foto'}
                </button>
              </div>
            )}

            {avatarError && <Hint className="text-danger mt-2">{avatarError}</Hint>}
          </div>
          <div className="flex-grow-1 w-100">
            <div className="row">
              <div className="col-md-6 mb-3">
                <FieldLabel className="text-muted">E-mail</FieldLabel>
                <FieldValue>{info.user?.email || '-'}</FieldValue>

                <FieldLabel className="text-muted mt-3">Celular</FieldLabel>
                <FieldValue>
                  {formatPhone(contact?.ddd_celular, contact?.numero_celular)}
                </FieldValue>

                <FieldLabel className="text-muted mt-3">Celular adicional</FieldLabel>
                <FieldValue>
                  {formatPhone(contact?.ddd_celular_adicional1, contact?.numero_celular_adicional1)}
                </FieldValue>
              </div>
              <div className="col-md-6 mb-3">
                <FieldLabel className="text-muted">Cargo</FieldLabel>
                <FieldValue>{contact?.role?.nome || '-'}</FieldValue>

                <FieldLabel className="text-muted mt-3">Gênero</FieldLabel>
                <FieldValue>{contact?.genre?.nome || '-'}</FieldValue>

                <FieldLabel className="text-muted mt-3">Telefone</FieldLabel>
                <FieldValue>{telefoneFixo}</FieldValue>
              </div>
            </div>
            <div className="d-flex flex-wrap mt-2">
              <button
                type="button"
                className="btn btn-primary mr-2 mb-2"
                onClick={() => onEdit && onEdit(info)}
              >
                <ButtonIcon className="material-icons align-middle mr-1">edit</ButtonIcon>
                Editar dados
              </button>

              <button
                type="button"
                className="btn btn-primary mb-2"
                onClick={() => {
                  setResetFeedback(null);
                  setShowResetModal(true);
                }}
              >
                <ButtonIcon className="material-icons align-middle mr-1">lock_reset</ButtonIcon>
                Resetar senha
              </button>

              {userProp?.ativo && profile?.id !== userProp?.id && (
                <button
                  type="button"
                  className="btn btn-outline-danger ml-auto mb-2"
                  onClick={() => {
                    setDeactivateError('');
                    setShowDeactivateModal(true);
                  }}
                >
                  <ButtonIcon className="material-icons align-middle mr-1">person_off</ButtonIcon>
                  Inativar usuário
                </button>
              )}
            </div>

            {resetFeedback && (
              <div
                className={`alert ${
                  resetFeedback.type === 'success' ? 'alert-success' : 'alert-danger'
                } py-2 mb-2 d-flex align-items-center`}
                role="alert"
              >
                <AlertIcon className="material-icons mr-2">
                  {resetFeedback.type === 'success' ? 'check_circle' : 'error'}
                </AlertIcon>
                {resetFeedback.message}
              </div>
            )}
          </div>
        </div>

        <FilterCard className="mt-4">
          <FilterHeader
            as="button"
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            className="d-flex align-items-center justify-content-between w-100 border-0 text-left py-3"
          >
            <div>
              <div className="font-weight-bold">
                Filtros{' '}
                {filters.length > 0 && <span className="text-muted">({filters.length})</span>}
              </div>
              <small className="text-muted">Marque os filtros que o usuário pode visualizar</small>
            </div>
            <Chevron className="material-icons" $isOpen={filtersOpen}>
              expand_more
            </Chevron>
          </FilterHeader>

          {filtersOpen &&
            (filters.length === 0 ? (
              <FilterEmpty className="text-muted">
                Nenhum filtro cadastrado para este usuário.
              </FilterEmpty>
            ) : (
              <>
                {(filters.length > 5 || visibleFilters.length > 1) && (
                  <div className="px-3 pt-3 d-flex align-items-center">
                    {filters.length > 5 && (
                      <input
                        type="text"
                        className="form-control form-control-sm my-3 mr-2"
                        placeholder="Buscar filtro pelo nome"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                      />
                    )}
                    {/* Com um filtro só, marcar direto é mais simples que o lote. */}
                    {visibleFilters.length > 1 && bulkTarget.ids.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm my-3 ml-auto text-nowrap"
                        onClick={() => {
                          setBulkError('');
                          setFilterConfirm({
                            kind: 'bulk',
                            grant: bulkTarget.grant,
                            ids: bulkTarget.ids,
                          });
                        }}
                        disabled={bulkSaving || pendingFilterId !== null}
                      >
                        {bulkSaving && 'Salvando…'}
                        {!bulkSaving && (bulkTarget.grant ? 'Selecionar todos' : 'Desmarcar todos')}
                      </button>
                    )}
                  </div>
                )}

                {bulkError && <small className="px-3 text-danger d-block">{bulkError}</small>}

                {visibleFilters.length === 0 ? (
                  <FilterEmpty className="text-muted">
                    Nenhum filtro corresponde à busca.
                  </FilterEmpty>
                ) : (
                  visibleFilters.map((filter) => (
                    <FilterRow key={filter.id}>
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id={`filter-${userProp?.id}-${filter.id}`}
                          checked={filter.has_access}
                          disabled={pendingFilterId === filter.id || bulkSaving}
                          onChange={() => {
                            setBulkError('');
                            setFilterConfirm({ kind: 'single', filter });
                          }}
                        />
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          className="custom-control-label"
                          htmlFor={`filter-${userProp?.id}-${filter.id}`}
                        >
                          {filter.name}
                        </label>
                      </div>

                      {pendingFilterId === filter.id && (
                        <FilterStatus className="ml-auto text-muted">salvando…</FilterStatus>
                      )}
                      {pendingFilterId !== filter.id && filterFeedback?.id === filter.id && (
                        <FilterStatus
                          className={`ml-auto ${
                            filterFeedback.type === 'saved' ? 'text-success' : 'text-danger'
                          }`}
                        >
                          <ButtonIcon className="material-icons align-middle mr-1">
                            {filterFeedback.type === 'saved' ? 'check_circle' : 'error'}
                          </ButtonIcon>
                          {filterFeedback.type === 'saved' ? 'Salvo' : 'Não salvo'}
                        </FilterStatus>
                      )}
                    </FilterRow>
                  ))
                )}
              </>
            ))}
        </FilterCard>

        <div className="mt-4">
          <h6 className="font-weight-bold mb-3">Preferências</h6>
          <div className="d-flex align-items-center mb-3">
            <StatusBadge
              className={`badge mr-2 ${recebeBoletim ? 'badge-success' : 'badge-secondary'}`}
            >
              {recebeBoletim ? 'Ativado' : 'Desativado'}
            </StatusBadge>
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id={`pref-boletim-${userProp?.id}`}
                checked={recebeBoletim}
                disabled={pendingPref === 'no_notification_bw'}
                onChange={() =>
                  togglePreference(
                    'no_notification_bw',
                    PREFERENCE_IDS.noNotificationBw,
                    info.no_notification_bw,
                  )
                }
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="custom-control-label" htmlFor={`pref-boletim-${userProp?.id}`}>
                Receba um e-mail com o link para acessar os novos boletins.
              </label>
            </div>
          </div>

          <div className="mb-3">
            <div className="font-weight-bold mb-2">Tipo de Notificação</div>

            <div className="custom-control custom-checkbox mb-2">
              <input
                type="checkbox"
                className="custom-control-input"
                id={`pref-email-${userProp?.id}`}
                checked={recebeEmail}
                disabled={pendingPref === 'no_bulletin_email_notification'}
                onChange={() =>
                  togglePreference(
                    'no_bulletin_email_notification',
                    PREFERENCE_IDS.noBulletinEmailNotification,
                    info.no_bulletin_email_notification,
                  )
                }
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="custom-control-label" htmlFor={`pref-email-${userProp?.id}`}>
                E-mail
              </label>
            </div>

            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id={`pref-push-${userProp?.id}`}
                checked={recebePush}
                disabled={pendingPref === 'no_bulletin_push_notification'}
                onChange={() =>
                  togglePreference(
                    'no_bulletin_push_notification',
                    PREFERENCE_IDS.noBulletinPushNotification,
                    info.no_bulletin_push_notification,
                  )
                }
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="custom-control-label" htmlFor={`pref-push-${userProp?.id}`}>
                Push Notification
              </label>
            </div>
          </div>

          {info.eligible_beta_tester_program && (
            <div className="card bg-light">
              <div className="card-body">
                <div className="font-weight-bold mb-2">Programa de Experiência do Usuário</div>

                <div className="d-flex align-items-center">
                  <StatusBadge
                    className={`badge mr-2 ${isBetaTester ? 'badge-success' : 'badge-secondary'}`}
                  >
                    {isBetaTester ? 'Ativado' : 'Desativado'}
                  </StatusBadge>

                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id={`pref-beta-${userProp?.id}`}
                      checked={isBetaTester}
                      disabled={pendingPref === 'beta_tester'}
                      onChange={() =>
                        togglePreference('beta_tester', PREFERENCE_IDS.betaTester, info.beta_tester)
                      }
                    />
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="custom-control-label" htmlFor={`pref-beta-${userProp?.id}`}>
                      Teste as novas funcionalidades antecipadamente e ajude a melhorar a
                      plataforma.
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal show={showResetModal} onHide={() => !resetting && setShowResetModal(false)}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Resetar senha</h5>
            <button
              type="button"
              className="close"
              aria-label="Fechar"
              onClick={() => !resetting && setShowResetModal(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <p className="mb-0">
              Um e-mail com instruções para redefinir a senha será enviado para{' '}
              <strong>{info.user?.email}</strong>. Deseja continuar?
            </p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowResetModal(false)}
              disabled={resetting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleResetPassword}
              disabled={resetting}
            >
              {resetting ? 'Enviando…' : 'Confirmar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showDeactivateModal}
        onHide={() => !deactivating && setShowDeactivateModal(false)}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Inativar usuário</h5>
            <button
              type="button"
              className="close"
              aria-label="Fechar"
              onClick={() => !deactivating && setShowDeactivateModal(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <p className="mb-0">
              <strong>{userProp?.nome}</strong> perderá o acesso à plataforma e deixará de receber
              os boletins. Deseja continuar?
            </p>
            {deactivateError && <p className="text-danger mb-0 mt-2">{deactivateError}</p>}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowDeactivateModal(false)}
              disabled={deactivating}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDeactivate}
              disabled={deactivating}
            >
              {deactivating ? 'Inativando…' : 'Inativar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal show={!!filterConfirm} onHide={() => setFilterConfirm(null)}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Confirmar alteração</h5>
            <button
              type="button"
              className="close"
              aria-label="Fechar"
              onClick={() => setFilterConfirm(null)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <p className="mb-0">{filterConfirmMessage}</p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setFilterConfirm(null)}
            >
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" onClick={handleFilterConfirm}>
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserProfilePanel;
